import React, { useState } from 'react';
import { Grid, Typography, Box, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationDialog from '../../../components/DeleteConfirmationDialog';
import MenuAnalysisModal from './MenuAnalysisModal';
import MenuAnalysisResults from './MenuAnalysisResults';
import FoodAnalysisActions from './FoodAnalysisActions';

const MenuAnalysisCard = ({ analysis, onDelete, onDownload, onViewDetails, analysisDetails }) => {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalAnalysisData, setModalAnalysisData] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'failed':
        return '#f44336';
      default:
        return '#FF9800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(analysis._id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!onDownload) return;

    setIsDownloading(true);
    try {
      await onDownload(analysis);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewDetails = () => {
    onViewDetails(analysis);
    setModalAnalysisData(analysisDetails);
    setShowDetailsModal(true);
  };

  const canDownload = analysis.status === 'completed';
  const canDelete = analysis.status === 'failed' || analysis.status === 'completed';

  return (
    <>
      <Box
        style={{
          marginBottom: '20px',
          padding: isMobile ? '16px' : '20px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          background: '#fff',
        }}
      >
        <Grid container spacing={isMobile ? 1 : 2}>
          {/* Image Section */}
          <Grid item xs={12} md={4}>
            <Box
              style={{
                width: '100%',
                height: isMobile ? '150px' : '200px',
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#f5f5f7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={analysis.imageUrl}
                alt={analysis.fileName || t('foodMenu.upload.imageAlt')}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Box
                style={{
                  display: 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                }}
              >
                <Typography variant="body2">{t('common.notAvailable')}</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Content Section */}
          <Grid item xs={12} md={8}>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '10px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : '0',
              }}
            >
              <Box>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  style={{
                    fontWeight: '600',
                    color: '#333',
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    lineHeight: '1.3',
                  }}
                >
                  {t('foodMenu.title')}
                </Typography>
                <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography
                    variant={isMobile ? 'body2' : 'subtitle2'}
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginRight: '8px',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                    }}
                  >
                    {t('foodAnalysisObj.status')}:
                  </Typography>
                  <Box
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(analysis.status),
                      marginRight: '6px',
                    }}
                  />
                  <span
                    style={{
                      color: getStatusColor(analysis.status),
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {t(`foodAnalysisObj.${analysis.status}`)}
                  </span>
                </Box>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FoodAnalysisActions
                  canDownload={canDownload}
                  canDelete={canDelete}
                  isDownloading={isDownloading}
                  isDeleting={isDeleting}
                  onViewDetails={handleViewDetails}
                  onDownload={handleDownload}
                  onDelete={() => setShowDeleteDialog(true)}
                  viewDetailsColor="#26786A"
                  downloadColor="#26786A"
                  deleteColor="#f44336"
                />
              </Box>
            </Box>

            <Typography
              variant="body2"
              style={{
                color: '#666',
                marginBottom: '15px',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              }}
            >
              {t('analyzedOn')} {formatDate(analysis.uploadDate)}
            </Typography>

            {analysis.status === 'pending' && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <CircularProgress size={16} style={{ color: '#26786a' }} />
                <Typography
                  variant="body2"
                  style={{
                    color: '#FF9800',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                  }}
                >
                  {t('foodAnalysisObj.analysisInProgress')}
                </Typography>
              </Box>
            )}

            <MenuAnalysisResults analysis={analysis} compact={true} />

            {analysis.status === 'failed' && analysis.errorMessage && (
              <Box style={{ marginTop: '10px' }}>
                <Typography
                  variant="body2"
                  style={{
                    color: '#f44336',
                    fontStyle: 'italic',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                  }}
                >
                  {t('foodAnalysisObj.error')}: {analysis.errorMessage}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('foodMenu.upload.deleteDialog.title')}
        message={t('foodMenu.upload.deleteDialog.message')}
        isLoading={isDeleting}
        loadingText={t('foodMenu.upload.deleteDialog.loading')}
      />

      <MenuAnalysisModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        analysisData={modalAnalysisData}
        isLoading={false}
      />
    </>
  );
};

export default MenuAnalysisCard;
